import {useMemo, useRef} from 'react'

import {getNumberOfDecimals, roundToDecimalsWithPow, roundToInt} from '@hconnect/tuc-processes'

type NumberStepCalculatorProps = {
  minValue: number
  maxValue: number
  stepValue: number
  initialValue?: number
  extraValues?: number[]
}

export type NumberStepCalculator = ReturnType<typeof createNumberStepCalculator>

export const createNumberStepCalculator = ({
  minValue,
  maxValue,
  stepValue,
  initialValue,
  extraValues = []
}: NumberStepCalculatorProps) => {
  const numberOfDecimals = getNumberOfDecimals(stepValue)
  const maxNumberOfDecimals = Math.max(
    getNumberOfDecimals(stepValue),
    getNumberOfDecimals(minValue),
    getNumberOfDecimals(maxValue),
    getNumberOfDecimals(initialValue || 0)
  )

  // Helper to check if value is step-aligned
  const isStepAligned = (value: number): boolean => {
    const intValue = roundToInt(value * Math.pow(10, maxNumberOfDecimals))
    const stepValueInt = roundToInt(stepValue * Math.pow(10, maxNumberOfDecimals))
    return intValue % stepValueInt === 0
  }

  // Find sanitized min: first step-aligned value >= minValue
  const sanitizedMin = (() => {
    if (isStepAligned(minValue)) return minValue

    const minValueInt = roundToInt(minValue * Math.pow(10, maxNumberOfDecimals))
    const stepValueInt = roundToInt(stepValue * Math.pow(10, maxNumberOfDecimals))
    const remainder = minValueInt % stepValueInt

    const nextValueInt = minValueInt - remainder + stepValueInt
    return roundToDecimalsWithPow(
      nextValueInt / Math.pow(10, maxNumberOfDecimals),
      numberOfDecimals
    )
  })()

  // Find sanitized max: last step-aligned value <= maxValue
  const sanitizedMax = (() => {
    if (isStepAligned(maxValue)) return maxValue

    const maxValueInt = roundToInt(maxValue * Math.pow(10, maxNumberOfDecimals))
    const stepValueInt = roundToInt(stepValue * Math.pow(10, maxNumberOfDecimals))
    const remainder = maxValueInt % stepValueInt

    const prevValueInt = maxValueInt - remainder
    return roundToDecimalsWithPow(
      prevValueInt / Math.pow(10, maxNumberOfDecimals),
      numberOfDecimals
    )
  })()

  // Generate range values (stepped values between sanitized min/max)
  const rangeValues: number[] = []
  let current = sanitizedMin
  const epsilon = stepValue / 100

  while (current <= sanitizedMax + epsilon) {
    rangeValues.push(roundToDecimalsWithPow(current, numberOfDecimals))
    current += stepValue
  }

  // Merge with extra values, deduplicate, and sort
  const allValues = [...rangeValues, ...extraValues]
  const uniqueValues = Array.from(
    new Set(allValues.map((v) => roundToDecimalsWithPow(v, numberOfDecimals)))
  )
  const allValidValues = uniqueValues.sort((a, b) => a - b)

  const validateValue = (valueToCheck: number): boolean => {
    // Check if value exists in allValidValues
    const tolerance = Math.pow(10, -(numberOfDecimals + 1))
    return allValidValues.some((validValue) => Math.abs(validValue - valueToCheck) < tolerance)
  }

  const getClosestValueInRange = (currentValue: number): number => {
    if (allValidValues.length === 0) return minValue

    // Find closest value - when equidistant, prefer higher value (like original implementation)
    let closest = allValidValues[0]
    let minDistance = Math.abs(currentValue - closest)

    for (const value of allValidValues) {
      const distance = Math.abs(currentValue - value)

      // Use small epsilon to handle floating point comparison
      const epsilon = Math.pow(10, -(numberOfDecimals + 2))

      if (distance < minDistance - epsilon) {
        // Clearly closer
        minDistance = distance
        closest = value
      } else if (Math.abs(distance - minDistance) < epsilon) {
        // Equidistant (within epsilon) - prefer higher value
        if (value > closest) {
          closest = value
          minDistance = distance
        }
      }
    }

    return closest
  }

  const getNextValue = (currentValue: number): number | undefined => {
    const normalizedValue = validateValue(currentValue)
      ? currentValue
      : getClosestValueInRange(currentValue)
    const tolerance = Math.pow(10, -(numberOfDecimals + 1))
    const currentIndex = allValidValues.findIndex((v) => Math.abs(v - normalizedValue) < tolerance)

    if (currentIndex === -1 || currentIndex === allValidValues.length - 1) {
      return undefined
    }

    return allValidValues[currentIndex + 1]
  }

  const getPreviousValue = (currentValue: number): number | undefined => {
    const normalizedValue = validateValue(currentValue)
      ? currentValue
      : getClosestValueInRange(currentValue)
    const tolerance = Math.pow(10, -(numberOfDecimals + 1))
    const currentIndex = allValidValues.findIndex((v) => Math.abs(v - normalizedValue) < tolerance)

    if (currentIndex <= 0) {
      return undefined
    }

    return allValidValues[currentIndex - 1]
  }

  const getSanitizedMinValue = (): number => {
    // Return first value from rangeValues (not extraValues)
    return rangeValues[0]
  }

  const getSanitizedMaxValue = (): number => {
    // Return last value from rangeValues (not extraValues)
    return rangeValues[rangeValues.length - 1]
  }

  const getAllValidValues = (): number[] => {
    // Return copy to prevent external mutations
    return [...allValidValues]
  }

  return {
    numberOfDecimals,
    getNextValue,
    getPreviousValue,
    getClosestValueInRange,
    validateValue,
    getSanitizedMinValue,
    getSanitizedMaxValue,
    getAllValidValues
  }
}

const useStableNumberArray = (values?: number[]) => {
  const ref = useRef<number[] | undefined>(values)

  const prev = ref.current

  const same =
    prev === values ||
    (!!prev &&
      !!values &&
      prev.length === values.length &&
      prev.every((v, i) => Object.is(v, values[i])))

  if (!same) {
    ref.current = values
  }

  return same ? prev : values
}

// Hook version with useMemo for performance
export const useNumberStepCalculator = (props: NumberStepCalculatorProps) => {
  const stableExtraValues = useStableNumberArray(props.extraValues)

  return useMemo(
    () =>
      createNumberStepCalculator({
        minValue: props.minValue,
        maxValue: props.maxValue,
        stepValue: props.stepValue,
        initialValue: props.initialValue,
        extraValues: stableExtraValues
      }),
    [props.minValue, props.maxValue, props.stepValue, props.initialValue, stableExtraValues]
  )
}
